import type React from 'react';
import isEqual from 'lodash/isEqual';

export function equalWithinEpsilon(a: number, b: number, epsilon = 0.001): boolean {
    return Math.abs(a - b) < epsilon;
}
export function greaterThanOrEqualWithinEpsilon(a: number, b: number, epsilon = 0.001): boolean {
    return a >= b || equalWithinEpsilon(a, b, epsilon);
}

export function lessThanOrEqualWithinEpsilon(a: number, b: number, epsilon = 0.001): boolean {
    return a <= b || equalWithinEpsilon(a, b, epsilon);
}

export function lessThanWithinEpsilon(a: number, b: number, epsilon = 0.001): boolean {
    return a < b - epsilon;
}

export function greaterThanWithinEpsilon(a: number, b: number, epsilon = 0.001): boolean {
    return a > b + epsilon;
}

/**
 * Compares two maps and returns if they're shallowly equal (ie. same properties which are equal
 * when shallowly compared)
 */
export function areEqualShallow<T extends Record<any, any>>(a: T, b: T): boolean {
    // if either are undefined, return false
    if (a === undefined || b === undefined) {
        return false;
    }
    // if the number of keys aren't the same, return false
    if (Object.keys(a).length !== Object.keys(b).length) {
        return false;
    }
    // compare each property value
    for (const key in a) {
        if (a[key] !== b[key]) {
            return false;
        }
    }
    return true;
}

/**
 * Memoized/Pure React Components don't re-render if their props are identical. This can be very helpful for
 * performance, especially since with Luminary-Redux-Connect we re-generate all luminaryStateToProps() props.
 *
 * The main issue we have is that any non-primitives will be new objects which fail the simple equality check (===),
 * meaning the component and all its children will be re-rendered.
 *
 * The solution we have is `cacheRef()`, which preserves the object reference as long as the object is deeply
 * equal or `createSelector()` if the data is coming from redux.
 *
 * This has one main issue: developers forget to call it!
 *
 * This validator should only run in development mode and will log an error to the console if we detect a case where
 * we're passing in different objects which are deeply identical.
 */
export function validatePropsInDev(
    component: React.ComponentType<any>,
    newProps: Readonly<Record<string, unknown>>,
    lastProps: Readonly<Record<string, unknown>>,
): void {
    if (!newProps || !lastProps) {
        return;
    }
    // if the number of properties aren't the same, return
    if (Object.keys(newProps).length !== Object.keys(lastProps).length) {
        // eslint-disable-next-line no-console
        console.warn('unable to cacheRefs due to length mismatch:', component);
        return;
    }
    // compare each property value
    for (const key in newProps) {
        if (typeof newProps[key] !== typeof lastProps[key]) {
            if (typeof newProps[key] === 'undefined' || typeof lastProps[key] === 'undefined') {
                // don't log warning for undefined
                return;
            }
            if (newProps[key] === null || lastProps[key] === null) {
                // don't log warning for null
                return;
            }
            // eslint-disable-next-line no-console
            console.warn(
                'type mismatch between renders for key',
                key,
                'in',
                component,
                ':',
                typeof newProps[key],
                typeof lastProps[key],
            );
            return;
        } else if (typeof newProps[key] === 'object' && newProps[key] !== lastProps[key]) {
            if (isEqual(newProps[key], lastProps[key])) {
                // eslint-disable-next-line no-console
                console.error(
                    'FOUND PERFORMANCE ISSUE. Appears that',
                    key,
                    'in',
                    component,
                    'is returning a ' +
                        'new object even though the contents are identical. This will result in the component ' +
                        're-rendering anytime anything in Luminary changes which is way too much! ' +
                        "If it's data coming from redux please use `createSelector()`. If it's data coming from Luminary " +
                        'please use `cacheRef()`.',
                );
            }
        }
    }
}
